import { animated, useSpring, useTransition } from "@react-spring/web";
import type { ReactElement, ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import { SliderDirection } from "../SliderDirection";

interface SliderProps {
  currentIndex: number;
  children: ReactElement[];
}

/**
 * Break glass: the track's `translate3d` offset is `currentIndex * 100%` run
 * through a spring, so it changes every frame — an inline style is the only place
 * it can land. The surrounding full-size boxes are part of the same scaffolding.
 */
function Slider(props: SliderProps) {
  const { currentIndex, children } = props;
  // This animation will slide the components horizontally
  const sliding = useSpring({
    to: { transform: `translate3d(-${currentIndex * 100}%, 0, 0)` },
    from: { transform: "translate3d(0%, 0, 0)" },
  });

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <animated.div
        style={{
          ...sliding,
          display: "flex",
          willChange: "transform",
        }}
      >
        {React.Children.map(children, (child, index) => (
          <div style={{ flex: "none", width: "100%" }} key={index}>
            {child}
          </div>
        ))}
      </animated.div>
    </div>
  );
}

interface Slider2Props {
  children: ReactNode[];
  currentIndex: number;
  direction?: SliderDirection | undefined;
  /** Floor height (px) so the absolutely-positioned pages always have room and
   *  the container can't collapse mid-transition. Lets callers reserve height
   *  without an inline `style` (banned in renovated feature code). */
  minHeight?: number | undefined;
}
/**
 * Break glass: each page's opacity and `translate3d` come from `useTransition`,
 * and the container's `minHeight` is measured from the live page — all runtime
 * numbers.
 */
export function Slider2({
  children,
  currentIndex,
  direction = SliderDirection.Right,
  minHeight,
}: Slider2Props) {
  const [height, setHeight] = useState(0);

  const ref = useRef<HTMLDivElement | null>(null);

  const transitions = useTransition(currentIndex, {
    initial: { opacity: 1, transform: "translate3d(0%,0,0)" },
    from: { opacity: 0, transform: `translate3d(${100 * direction}%,0,0)` },
    enter: { opacity: 1, transform: "translate3d(0%,0,0)" },
    leave: {
      opacity: 0,
      transform: `translate3d(${-100 * direction}%,0,0)`,
    },
    config: { tension: 350, friction: 60 },
  });

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight);
    }
  }, [ref.current?.clientHeight, minHeight]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: Math.max(height, minHeight ?? 0),
        // Clip the pages as they slide in/out so a leaving page doesn't bleed
        // past the panel edge into the margin.
        overflow: "hidden",
      }}
    >
      {transitions((animationStyle, i) => (
        <animated.div
          style={{
            ...animationStyle,
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
        >
          <div ref={ref}>{children[i]}</div>
        </animated.div>
      ))}
    </div>
  );
}

export default Slider;
